import EmojiResource from '@atlaskit/emoji/emoji-resource';
import type { EmojiResourceConfig, Retry } from '@atlaskit/emoji/emoji-resource';
import type {
	EmojiDescription,
	EmojiId,
	EmojiProviderLookupOrder,
	EmojiResponse,
	OptionalEmojiDescription,
	OptionalEmojiDescriptionWithVariations,
	SearchOptions,
} from '@atlaskit/emoji/types';

type ProviderConfig = EmojiResourceConfig['providers'][number];

const isMediaRepresentation = (representation: EmojiDescription['representation']): boolean =>
	!!representation && 'mediaPath' in representation;

/**
 * For a config with `options.onlyFetchOnDemand`: fetches the full emoji repository (every provider in
 * the config) when something needs it, such as a picker, typeahead, shortName lookup or ascii map.
 * Until then, id lookups go to `singleEmojiApi`, so a page that only displays emoji never downloads
 * the repository.
 */
export class OnDemandEmojiResource extends EmojiResource {
	// filter() reports "not ready" without waiting, so it has to start the fetch itself. The fetch
	// re-runs the last filter when it completes.
	filter(query?: string, options?: SearchOptions): void {
		this.startRepositoryFetch();
		super.filter(query, options);
	}

	async fetchByEmojiId(
		emojiId: EmojiId,
		optimistic: boolean,
		emojiProviderLookupOrder?: EmojiProviderLookupOrder,
	): Promise<OptionalEmojiDescriptionWithVariations> {
		if (!this.emojiProviderConfig.singleEmojiApi || !emojiId.id || this.isRepositoryLoaded()) {
			return super.fetchByEmojiId(emojiId, optimistic, emojiProviderLookupOrder);
		}

		// Emoji ids never contain a slash, but page icons can be URLs, such as a favicon. No emoji list
		// has them, and the single-emoji endpoint rejects them, which would download the repository.
		if (emojiId.id.includes('/')) {
			return undefined;
		}

		// With `optimistic` true, the base class requests just this emoji from singleEmojiApi instead of
		// waiting for the repository. ResourcedEmoji passes its `optimistic` prop, which
		// @atlaskit/reactions leaves off, so force it on until the repository has loaded.
		const emoji = await super.fetchByEmojiId(emojiId, true, emojiProviderLookupOrder);
		// The single-emoji request falls back to the repository when it fails, but not when it finds
		// nothing. With `optimistic` false, the base class waits for the repository, and
		// retryIfLoading starts downloading it.
		return emoji ?? super.fetchByEmojiId(emojiId, false, emojiProviderLookupOrder);
	}

	findById(id: string): OptionalEmojiDescription | Promise<OptionalEmojiDescription> {
		if (!this.emojiProviderConfig.singleEmojiApi || this.isRepositoryLoaded()) {
			return super.findById(id);
		}

		return this.fetchByEmojiId({ id, shortName: '' }, true);
	}

	protected retryIfLoading<T>(retry: Retry<T>, defaultResponse: T): Promise<T> {
		this.startRepositoryFetch();
		return super.retryIfLoading(retry, defaultResponse);
	}

	// The single-emoji response can be the first to carry a media token. SiteEmojiResource keeps the
	// URL it is created with for uploads, deletes and token refreshes, so it must get the site provider
	// rather than the single-emoji URL. The base class primes the resource with the first emoji, which
	// throws for a standard or Atlassian emoji and makes the lookup fall back to the repository, so only
	// media emoji are passed on.
	protected initSiteEmojiResource(
		emojiResponse: EmojiResponse,
		provider: ProviderConfig,
	): Promise<void> {
		const siteProvider = this.emojiProviderConfig.providers.find(({ url }) =>
			url.endsWith('/site'),
		);
		return super.initSiteEmojiResource(
			{
				...emojiResponse,
				emojis: emojiResponse.emojis.filter(({ representation }) =>
					isMediaRepresentation(representation),
				),
			},
			siteProvider ?? provider,
		);
	}

	private isRepositoryLoaded(): boolean {
		return !!this.emojiRepository && this.initialLoaders !== 0 && this.activeLoaders === 0;
	}

	private startRepositoryFetch(): void {
		if (!this.isInitialised) {
			// Failures are reported through notifyError and the pending retries.
			this.fetchEmojiProvider().catch(() => {});
		}
	}
}
